import { useLanguage } from "@/i18n/LanguageContext";
import LongPage from "@/components/LongPage";
import {
  PlatformLoopDiagram,
  DataModelDiagram,
  LayersDiagram,
  ProcessTrack,
  ScalePath,
} from "@/components/platform/Diagrams";

export default function PlatformPage() {
  const { t } = useLanguage();
  return (
    <LongPage
      eyebrow={t("Platform")}
      title={t("How the BEEDIGIT platform works")}
      description={t("BEEDIGIT connects business systems, production equipment and shop-floor teams in one operational loop. Plans arrive from ERP, operators and machines execute the work, and verified production facts return to management systems in real time.")}
      seoTitle="How the BEEDIGIT Platform Works: Architecture"
      seoDescription="See how BEEDIGIT turns ERP plans into shop-floor execution: four connected layers, a shared manufacturing data model and verified results returned to business systems."
      bullets={[
        "Business systems, execution, shop floor and improvement in one loop",
        "Every event contextualised by order, machine, shift, product and batch",
        "Start on one bottleneck and expand without rebuilding",
        "Verified results returned to ERP, BI and operational systems",
      ]}
      primaryCta={{ label: "Book a Demo", to: "/get-started" }}
      secondaryCta={{ label: "Explore the architecture", to: "#architecture" }}
      heroVisual={<PlatformLoopDiagram />}
      ctaHeading="See how BEEDIGIT would fit your factory"
      ctaBody="Walk us through your production flow, equipment and existing systems. We will show how BEEDIGIT can connect the process from planning to execution and verified results."
      ctaSecondary={{ label: "Explore Modules", to: "/modules" }}
      sections={[
        {
          id: "architecture",
          kind: "prose",
          eyebrow: "Architecture",
          heading: "One platform, four connected layers",
          body: (
            <div className="space-y-6">
              <p className="text-muted-foreground max-w-3xl">
                {t("Each layer has a clear job and hands a defined set of data to the next one. Nothing is re-keyed between them, so the plan, the execution and the reported result all describe the same production event.")}
              </p>
              <LayersDiagram
                layers={[
                  {
                    name: "Business systems",
                    what: "Production plans, master data, products, routings, customers and job orders live where they are already maintained — usually ERP or an APS.",
                    moves:
                      "Job orders, planned quantities and dates, product and routing master data, work-center calendars.",
                  },
                  {
                    name: "Execution",
                    what: "Job dispatch, operator workflows, production confirmations, quality checks, material movements and maintenance activities happen against the received plan.",
                    moves:
                      "Work instructions and active jobs down to the shop floor; confirmed output, scrap, consumption and event context back up.",
                  },
                  {
                    name: "Shop floor",
                    what: "Machines, PLCs, sensors, meters, BEEDIGIT hardware, industrial tablets, scanners and printers generate and capture the physical reality of production.",
                    moves:
                      "Counts, cycle and speed signals, states and stops, scans, energy readings and operator inputs.",
                  },
                  {
                    name: "Improvement",
                    what: "Real-time dashboards, OEE, downtime analysis, quality, energy, traceability and continuous-improvement actions turn events into decisions.",
                    moves:
                      "Structured KPIs and verified facts to management, BI and back into the next production plan.",
                  },
                ]}
              />
            </div>
          ),
        },
        {
          id: "workflow",
          kind: "prose",
          eyebrow: "Operational loop",
          heading: "From production plan to verified result",
          body: (
            <div className="space-y-6">
              <p className="text-muted-foreground max-w-3xl">
                {t("The same seven steps repeat for every order, on every shift. They are what makes the reported number match what actually happened on the line.")}
              </p>
              <ProcessTrack
                steps={[
                  { n: 1, title: "Receive the plan", body: "Orders, quantities and dates arrive from ERP or APS." },
                  { n: 2, title: "Dispatch work", body: "Jobs reach the right work center, terminal and operator." },
                  { n: 3, title: "Collect data", body: "Machine signals and operator confirmations are captured as they happen." },
                  { n: 4, title: "Contextualise", body: "Every event is tied to order, product, machine, shift and batch." },
                  { n: 5, title: "Detect deviations", body: "Stops, slow running, scrap and quality issues surface with a reason." },
                  { n: 6, title: "Confirm back", body: "Actual output, scrap and consumption return to business systems." },
                  { n: 7, title: "Improve", body: "Trusted history drives loss analysis and the next improvement action." },
                ]}
              />
            </div>
          ),
        },
        {
          id: "data-model",
          kind: "prose",
          eyebrow: "Data model",
          heading: "A shared manufacturing data model",
          body: (
            <div className="space-y-6">
              <p className="text-muted-foreground max-w-3xl">
                {t("In most factories this information lives in separate spreadsheets, machine screens and modules. BEEDIGIT keeps it in one model, so a downtime minute, a scrapped unit and a kilowatt-hour can all be read against the same order and shift.")}
              </p>
              <DataModelDiagram />
            </div>
          ),
        },
        {
          id: "roles",
          kind: "personas",
          eyebrow: "Roles",
          heading: "Built for every production role",
          intro:
            "The same underlying data is presented differently depending on who is looking at it.",
          items: [
            { role: "Operator", pain: "Unclear what to run next and how to report it.", value: "The active job, instructions and the confirmations required at the terminal." },
            { role: "Shift supervisor", pain: "Status is discovered at the end of the shift.", value: "Live status, delays, downtime and progress against the shift target." },
            { role: "Production manager", pain: "Plan versus actual assembled manually.", value: "Comparison across lines, shifts and orders from one source." },
            { role: "Maintenance", pain: "Breakdowns reported late and without context.", value: "Machine events and downtime connected to maintenance actions." },
            { role: "Quality", pain: "Results hard to trace back to a batch.", value: "Inspections and non-conformances linked to the correct batch and order." },
            { role: "Management", pain: "Waiting for manual reports.", value: "Structured, trusted production facts as they are confirmed." },
          ],
        },
        {
          id: "scale",
          kind: "prose",
          eyebrow: "Adoption",
          heading: "Start small, expand without rebuilding",
          body: (
            <div className="space-y-6">
              <p className="text-muted-foreground max-w-3xl">
                {t("Most rollouts begin with one measurable problem — a bottleneck machine, unexplained downtime or unreliable output reporting. Each following step reuses the same foundation, so scope grows without a new implementation project.")}
              </p>
              <ScalePath
                stages={[
                  { title: "One bottleneck", body: "Monitor the constraint and make its real losses visible." },
                  { title: "One work center", body: "Add operator workflows and confirmations around it." },
                  { title: "One line", body: "Connect upstream and downstream machines into one flow." },
                  { title: "More modules", body: "Add quality, material, maintenance or energy scope." },
                  { title: "More departments", body: "Extend to planning, maintenance, quality and management users." },
                  { title: "Multiple plants", body: "Compare sites on the same definitions and master data." },
                ]}
              />
            </div>
          ),
        },
        {
          id: "ecosystem",
          kind: "related",
          eyebrow: "Ecosystem",
          heading: "Explore the BEEDIGIT ecosystem",
          intro: "Where to go next, depending on what you need to evaluate.",
          items: [
            { title: "Modules", href: "/modules", desc: "Open this if you need to know exactly which operational capabilities are covered — monitoring, job orders, quality, material, maintenance, energy." },
            { title: "Hardware", href: "/hardware", desc: "Open this if the question is how machines and operators actually get connected: gateways, terminals, scanners and printers." },
            { title: "Integrations", href: "/integrations", desc: "Open this if your ERP, BI or maintenance systems have to stay in place and you need to know what data moves each way." },
            { title: "Industries", href: "/industries", desc: "Open this to see the workflows, signals and losses that matter in your specific manufacturing environment." },
          ],
        },
        {
          id: "reliability",
          kind: "features",
          eyebrow: "Operations",
          heading: "Security and reliability in practical terms",
          intro:
            "What this means day to day, without over-promising: the exact scope is confirmed during design with your IT and OT teams.",
          cols: 2,
          items: [
            { title: "Data transport", desc: "Shop-floor data is transported over encrypted industrial protocols and HTTPS APIs between the plant, the platform and connected systems." },
            { title: "Roles and permissions", desc: "Operators, supervisors, maintenance, quality and management see and confirm only what their role allows." },
            { title: "Event history", desc: "Confirmations, downtime, quality results and material movements are stored as time-stamped events that can be reviewed later." },
            { title: "Integration boundaries", desc: "Each business object has one defined owner system, one direction of truth and an agreed write scope." },
            { title: "Local continuity", desc: "Where the deployment includes local hardware, data collection continues during a network interruption and is transmitted when the link is restored." },
            { title: "Data for analytics", desc: "Production, quality, maintenance and energy data remain available for reporting and BI in a structured, exportable form." },
          ],
        },
      ]}
    />
  );
}